import React, { useEffect, useState } from "react";
import {
  Box,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import IntegrationApi from "../../apis/integrationApi";
import { NEUTRAL, ORANGE, RADIUS, VIOLET } from "../../styles/brandTokens";
import type { TheoOverview, TimeSavedModel } from "../../types/integration";
import { formatCount, text } from "../monitor/SummaryPanel";
import { formatHours, methodologyRows } from "./theoOverviewModel";

const integrationApi = new IntegrationApi();

const headCellSx = {
  ...text(11.5, NEUTRAL.textSecondary, 700),
  letterSpacing: "0.06em",
  textTransform: "uppercase" as const,
  borderBottom: `1px solid ${NEUTRAL.border}`,
  py: 1,
};
const cellSx = { ...text(13.5), borderBottom: `1px solid ${NEUTRAL.border}`, verticalAlign: "top", py: 1.25 };
const numberSx = { ...cellSx, textAlign: "right" as const, fontVariantNumeric: "tabular-nums", whiteSpace: "nowrap" as const };
const totalSx = { ...numberSx, ...text(13.5, NEUTRAL.text, 700), borderBottom: 0 };

function useTimeSavedModel(open: boolean) {
  const [model, setModel] = useState<TimeSavedModel | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    if (!open || model) return undefined;
    let active = true;
    setError(null);
    integrationApi
      .getTimeSavedModel()
      .then((data) => {
        if (active) setModel(data);
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof Error ? err.message : "Couldn't load how time saved is estimated.");
      });
    return () => {
      active = false;
    };
  }, [open, model]);
  return { model, error };
}

const MappingTable: React.FC<{ model: TimeSavedModel; overview: TheoOverview | null }> = ({ model, overview }) => {
  const rows = methodologyRows(model, overview);
  const totalActions = rows.reduce((sum, row) => sum + row.actions, 0);
  const totalMinutes = rows.reduce((sum, row) => sum + row.minutes, 0);
  return (
    <Box sx={{ overflowX: "auto", mx: -1 }}>
      <Table size="small" aria-label="Minutes saved per action type" sx={{ minWidth: 560 }}>
        <TableHead>
          <TableRow>
            <TableCell sx={headCellSx}>Action type</TableCell>
            <TableCell sx={{ ...headCellSx, textAlign: "right" }}>Minutes each</TableCell>
            <TableCell sx={{ ...headCellSx, textAlign: "right" }}>Actions in view</TableCell>
            <TableCell sx={{ ...headCellSx, textAlign: "right" }}>Time saved</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <TableRow key={row.category}>
              <TableCell sx={cellSx}>
                <Typography sx={text(13.5, NEUTRAL.text, 600)}>{row.label}</Typography>
                <Typography sx={{ ...text(12.5, NEUTRAL.textSecondary), mt: 0.25 }}>{row.covers}</Typography>
                <Typography sx={{ ...text(12, NEUTRAL.textMuted), mt: 0.25 }}>e.g. {row.example}</Typography>
              </TableCell>
              <TableCell sx={{ ...numberSx, ...text(13.5, VIOLET.theoText, 700) }}>{row.minutesPerAction} min</TableCell>
              <TableCell sx={numberSx}>{formatCount(row.actions)}</TableCell>
              <TableCell sx={numberSx}>{row.actions ? formatHours(row.minutes) : "—"}</TableCell>
            </TableRow>
          ))}
        </TableBody>
        {overview?.actions ? (
          <TableFooter>
            <TableRow>
              <TableCell sx={{ ...cellSx, ...text(13.5, NEUTRAL.text, 700), borderBottom: 0 }}>Total in this view</TableCell>
              <TableCell sx={totalSx} />
              <TableCell sx={totalSx}>{formatCount(totalActions)}</TableCell>
              <TableCell sx={totalSx}>{formatHours(totalMinutes)}</TableCell>
            </TableRow>
          </TableFooter>
        ) : null}
      </Table>
    </Box>
  );
};

const TimeSavedDialog: React.FC<{ open: boolean; onClose: () => void; overview: TheoOverview | null }> = ({
  open,
  onClose,
  overview,
}) => {
  const { model, error } = useTimeSavedModel(open);
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      aria-labelledby="time-saved-title"
      PaperProps={{ sx: { borderRadius: `${RADIUS.well}px` } }}
    >
      <DialogTitle id="time-saved-title" sx={{ ...text(20, NEUTRAL.text, 700), pr: 6 }}>
        How we estimate time saved
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: "absolute", right: 12, top: 12, color: NEUTRAL.textSecondary, borderRadius: `${RADIUS.control}px` }}
        >
          <CloseRoundedIcon fontSize="small" />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        <Typography sx={{ ...text(14, NEUTRAL.textSecondary), mb: 1.5 }}>
          Theo logs every action it completes. Each action is matched to a type below, and each type stands in for a
          fixed number of hands-on technician minutes.
        </Typography>
        <Box
          sx={{
            ...text(14, NEUTRAL.text, 600),
            background: VIOLET.wash,
            border: `1px solid ${VIOLET.tint}`,
            borderRadius: `${RADIUS.card}px`,
            px: 2,
            py: 1.25,
            mb: 2.5,
          }}
        >
          Time saved = actions completed × minutes per action, added up across every type
        </Box>

        {error ? (
          <Typography role="alert" sx={text(13.5, ORANGE.text)}>
            {error}
          </Typography>
        ) : !model ? (
          <CircularProgress size={22} sx={{ color: VIOLET.main }} aria-label="Loading the estimate" />
        ) : (
          <>
            <MappingTable model={model} overview={overview} />
            <Typography component="h3" sx={{ ...text(15, NEUTRAL.text, 700), mt: 3, mb: 1 }}>
              Assumptions
            </Typography>
            <Box component="ul" sx={{ m: 0, pl: 2.5, display: "grid", gap: 0.75 }}>
              {model.assumptions.map((assumption) => (
                <Box component="li" key={assumption} sx={text(13.5, NEUTRAL.textSecondary)}>
                  {assumption}
                </Box>
              ))}
            </Box>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default TimeSavedDialog;
